// apps/web/src/pages/author/constraint-config/cardinality-config.tsx
//
// Phase 9 Plan 07 Task 1 — Cardinality constraint authoring sub-form
// (CSTR-01 partial + CSTR-05 explanation surface).
//
// Schema source-of-truth (packages/shared/src/schemas/constraint.ts lines 28-35):
//   { kind: "cardinality";
//     resource: "recipe-in-spread" | "option-in-widget";
//     scope: string min length 1;
//     min?: nonneg int;
//     max: positive int;
//     explanation: 1-500 chars }
//
// UI-SPEC §"Cardinality" copy table (lines 467-475):
//   Helper: "Limits how many options can be selected at once."
//   Min label: "Minimum selections"
//   Max label: "Maximum selections"
//   Scope label: "Applies to" — Select with options
//     "This {subdivision.lower}" / "All {subdivision.lower}s in this {container.lower}"
//     when resource = "recipe-in-spread"; plain Input (widget-instance-id) when
//     resource = "option-in-widget".
//
// authr-06 hygiene: per-path useWatch ONLY. NEVER top-level.
// authr-03 hygiene: no useFieldArray on cardinality (no sub-arrays).
//
// Vocabulary terms are resolved via useTerm() from
// apps/web/src/lib/vocabulary/useTerm.ts (Plan 09-04). The host
// SpreadVocabularyProvider supplies overrides; defaults come from
// VOCABULARY_DEFAULTS in @the-recipe-spread/shared.

import { useFormContext, useWatch } from "react-hook-form";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useTerm } from "@/lib/vocabulary/useTerm.js";
import type { ConstraintConfigSubFormProps } from "./types.js";

// =============================================================================
// CardinalityConfigShape — local typing for the default factory.
// =============================================================================

export interface CardinalityConfigShape {
  kind: "cardinality";
  resource: "recipe-in-spread" | "option-in-widget";
  scope: string;
  min?: number;
  max: number;
  explanation: string;
}

// =============================================================================
// Default factory
// =============================================================================
//
// CSTR-01 v1 cardinality default: scope/explanation are "" — the user fills
// them in via the form (matches the Phase 8 widget-config factory convention).
// max defaults to 1 (single-select). Same convention as
// defaultSelectabilityConfig in selectability-config.tsx — base factory fails
// .min(1) on scope + explanation; tests inject realistic values before
// safeParse.

export function defaultCardinalityConfig(): CardinalityConfigShape {
  return {
    kind: "cardinality",
    resource: "option-in-widget",
    scope: "",
    max: 1,
    explanation: "",
  };
}

// =============================================================================
// CardinalityConfigSubForm
// =============================================================================

export function CardinalityConfigSubForm({
  constraintId: _constraintId,
  basePath,
}: ConstraintConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  // Per-path useWatch — read only the resource field at this constraint.
  const resource = useWatch({ control, name: `${basePath}.resource` }) as
    | "recipe-in-spread"
    | "option-in-widget"
    | undefined;

  // Vocabulary substitution for scope Select labels when resource =
  // recipe-in-spread (UI-SPEC line 475).
  const subdivision = useTerm("subdivision");
  const container = useTerm("container");
  const subdivisionLower = subdivision.toLowerCase();
  const containerLower = container.toLowerCase();

  const isRecipeInSpread = resource === "recipe-in-spread";

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="CARDINALITY">
        <p className="text-xs text-muted-foreground">
          Limits how many options can be selected at once.
        </p>

        <FormField
          control={control}
          name={`${basePath}.resource`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.resource`}>
              <FormLabel>Resource</FormLabel>
              <Select
                value={(f.value as string | undefined) ?? "option-in-widget"}
                onValueChange={f.onChange}
              >
                <FormControl>
                  <SelectTrigger data-testid="cardinality-resource">
                    <SelectValue placeholder="Pick a resource" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="option-in-widget">An option in a widget</SelectItem>
                  <SelectItem value="recipe-in-spread">
                    A recipe in this {containerLower}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${basePath}.scope`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.scope`}>
              <FormLabel>Applies to</FormLabel>
              {isRecipeInSpread ? (
                <Select value={(f.value as string | undefined) ?? ""} onValueChange={f.onChange}>
                  <FormControl>
                    <SelectTrigger data-testid="cardinality-scope">
                      <SelectValue placeholder={`Pick a scope`} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="this-batch-set">This {subdivisionLower}</SelectItem>
                    <SelectItem value="all-batch-sets-in-spread">
                      All {subdivisionLower}s in this {containerLower}
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="widget-instance-id"
                    data-testid="cardinality-scope"
                  />
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${basePath}.min`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.min`}>
              <FormLabel>Minimum selections</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={f.value === undefined || f.value === null ? "" : String(f.value)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    f.onChange(raw === "" ? undefined : Number(raw));
                  }}
                  onBlur={f.onBlur}
                  name={f.name}
                  ref={f.ref}
                  data-testid="cardinality-min"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${basePath}.max`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.max`}>
              <FormLabel>Maximum selections</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={f.value === undefined || f.value === null ? "" : String(f.value)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    f.onChange(raw === "" ? undefined : Number(raw));
                  }}
                  onBlur={f.onBlur}
                  name={f.name}
                  ref={f.ref}
                  data-testid="cardinality-max"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>

      <Fieldset legend="EXPLANATION">
        <FormField
          control={control}
          name={`${basePath}.explanation`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.explanation`}>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  rows={3}
                  maxLength={500}
                  placeholder="Shown to authors and viewers when this rule disables an option. Required."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
CardinalityConfigSubForm.displayName = "CardinalityConfigSubForm";
